import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Code, Briefcase, GraduationCap, Sparkles } from 'lucide-react';

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const milestones = [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'Latar Belakang Hukum',
      desc: 'Sarjana Hukum (S.H) dengan pemahaman mendalam tentang regulasi dan compliance',
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: 'No-Code Development',
      desc: 'Ahli dalam membangun aplikasi menggunakan Bubble, Webflow, dan platform no-code lainnya',
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: 'AI Prompt Engineering',
      desc: 'Spesialisasi dalam merancang prompt yang menghasilkan output AI berkualitas tinggi',
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'Digital Marketing & Web3',
      desc: 'Berpengalaman dalam strategi digital marketing dan teknologi blockchain',
    },
  ];

  return (
    <section id="about" ref={ref} className="py-20 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-neon-blue/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-neon-green/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="neon-text-blue">Tentang</span> <span className="text-white">Saya</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-blue to-neon-green mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Story */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold text-neon-blue mb-4">
                Dari Pasal ke Pixel
              </h3>
              <div className="space-y-4 text-gray-300 text-justify">
                <p>
                  Perjalanan saya dimulai dari dunia hukum, di mana saya belajar tentang struktur, logika, dan pemecahan masalah yang sistematis. Namun, passion saya terhadap teknologi membawa saya untuk mengeksplorasi dunia digital.
                </p>
                <p>
                  Dengan latar belakang hukum yang kuat, saya memahami pentingnya compliance, keamanan data, dan regulasi dalam pengembangan produk digital. Ini memberikan perspektif unik dalam setiap projek yang saya kerjakan.
                </p>
                <p>
                  Saat ini, saya fokus pada No-Code Development dan AI Prompt Engineering. Saya percaya bahwa teknologi harus accessible untuk semua orang, dan no-code adalah kunci untuk demokratisasi pengembangan aplikasi.
                </p>
                <p>
                  Melalui AI Prompt Engineering, saya membantu bisnis dan individu memanfaatkan kekuatan AI untuk meningkatkan produktivitas dan kreativitas mereka.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Milestones */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-4"
          >
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                whileHover={{ scale: 1.02, x: 10 }}
                className="glass-card p-6 flex items-start gap-4"
              >
                <div className="p-3 rounded-xl bg-neon-blue/10 text-neon-blue">
                  {milestone.icon}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">{milestone.title}</h4>
                  <p className="text-gray-400 text-sm">{milestone.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
